import { assert } from '@sparkdotfi/common-universal'
import { UseMutationResult, useMutation } from '@tanstack/react-query'
import { useConfig } from 'wagmi'
import { switchChain } from 'wagmi/actions'

import { useMainWagmiConfig } from '@/config/wagmi/WagmiConfigProvider'
import { useSandbox } from '@/domain/sandbox/useSandbox'
import { useWatchWalletModeActions } from '@/domain/wallet/useWatchWalletModeActions'

export interface UseNetworkChangeParams {
  onSuccess?: () => void
}

export type UseNetworkChangeResult = Omit<UseMutationResult<void, Error, number, unknown>, 'mutate' | 'mutateAsync'> & {
  changeNetwork: (chainId: number) => void
  changeNetworkAsync: (chainId: number) => Promise<void>
}

export function useNetworkChange(parameters: UseNetworkChangeParams = {}): UseNetworkChangeResult {
  const mainConfig = useMainWagmiConfig()
  const wagmiConfig = useConfig()
  const { disableWatchWalletMode } = useWatchWalletModeActions()
  const { sandboxChainId, isInSandbox, enableSandbox, disableSandbox } = useSandbox()

  async function changeNetwork(chainId: number): Promise<void> {
    // switching to sandbox
    if (chainId === sandboxChainId) {
      disableWatchWalletMode()
      enableSandbox()
      return
    }

    // switching from sandbox
    if (isInSandbox) {
      assert(mainConfig, 'Main wagmi config is not available')
      disableSandbox()
      await switchChain(mainConfig, { chainId })
      return
    }

    await switchChain(wagmiConfig, { chainId })
  }

  const mutation = useMutation({
    mutationFn: changeNetwork,
    ...parameters,
  })

  return {
    changeNetwork: mutation.mutate,
    changeNetworkAsync: mutation.mutateAsync,
    ...mutation,
  }
}
